import {
  signOut as firebaseSignOut,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithCustomToken,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { auth, existingAuth, functions } from '../services/firebase';
import { ensureUserRegistered } from '../services/registration';

interface AuthContextType {
  /** 新プロジェクトのユーザー（メインで使う） */
  user: User | null;
  /** 認証状態の読み込み中 */
  loading: boolean;
  /** Google認証コードでログイン（リダイレクト方式用） */
  signInWithGoogleCode: (code: string) => Promise<void>;
  /** Appleの一時コードでログイン */
  signInWithAppleCode: (tempCode: string, clientId: string) => Promise<void>;
  /** ログアウト */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isExchangingTokenRef = useRef(false);

  /**
   * トークン交換処理
   * 既存プロジェクトのIDトークンを新プロジェクトのカスタムトークンに交換
   */
  const exchangeToken = useCallback(async (existingUser: User) => {
    // 既に新プロジェクトに同じユーザーでログイン済みならスキップ
    if (auth.currentUser?.uid === existingUser.uid) return;
    if (isExchangingTokenRef.current) return;

    isExchangingTokenRef.current = true;
    try {
      const idToken = await existingUser.getIdToken();
      const exchangeTokenFn = httpsCallable<{ idToken: string }, { customToken: string }>(functions, 'exchangeToken');
      const result = await exchangeTokenFn({ idToken });
      await signInWithCustomToken(auth, result.data.customToken);

      // 新規ユーザーの場合は登録処理を実行
      await ensureUserRegistered(existingUser.uid);
    } catch (error) {
      console.error('Token exchange failed:', error);
    } finally {
      isExchangingTokenRef.current = false;
    }
  }, []);

  // 既存プロジェクトの認証状態を監視し、トークン交換を実行
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(existingAuth, async (existingUser) => {
      if (existingUser) {
        await exchangeToken(existingUser);
      }
    });

    return () => unsubscribe();
  }, [exchangeToken]);

  // 新プロジェクトの認証状態を監視（メインの状態）
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (newUser) => {
      setUser(newUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 初期ロード時に既存プロジェクトのユーザーがいればトークン交換
  useEffect(() => {
    const checkExistingAuth = async () => {
      const existingUser = existingAuth.currentUser;
      if (existingUser && !auth.currentUser) {
        await exchangeToken(existingUser);
      }
    };
    checkExistingAuth();
  }, [exchangeToken]);

  const signInWithGoogleCode = async (code: string) => {
    try {
      const signInWithGoogleFn = httpsCallable<
        { code: string; redirectUri: string },
        { existingCustomToken: string; mainCustomToken: string }
      >(functions, 'signInWithGoogle');
      const redirectUri = window.location.origin + window.location.pathname;
      const result = await signInWithGoogleFn({ code, redirectUri });

      // 両プロジェクトに並列でサインイン
      const [existingUserCredential] = await Promise.all([
        signInWithCustomToken(existingAuth, result.data.existingCustomToken),
        signInWithCustomToken(auth, result.data.mainCustomToken),
      ]);

      // 新規ユーザーの場合は登録処理を実行
      await ensureUserRegistered(existingUserCredential.user.uid);
    } catch (error) {
      console.error('Google sign-in failed:', error);
      throw error;
    }
  };

  const signInWithAppleCode = async (tempCode: string, clientId: string) => {
    try {
      const signInWithAppleFn = httpsCallable<
        { tempCode: string; clientId: string },
        { existingCustomToken: string; mainCustomToken: string }
      >(functions, 'signInWithApple');
      const result = await signInWithAppleFn({ tempCode, clientId });

      // 両プロジェクトに並列でサインイン
      const [existingUserCredential] = await Promise.all([
        signInWithCustomToken(existingAuth, result.data.existingCustomToken),
        signInWithCustomToken(auth, result.data.mainCustomToken),
      ]);

      // 新規ユーザーの場合は登録処理を実行
      await ensureUserRegistered(existingUserCredential.user.uid);
    } catch (error) {
      console.error('Apple sign-in failed:', error);
      throw error;
    }
  };

  const signOut = async () => {
    await Promise.all([firebaseSignOut(existingAuth), firebaseSignOut(auth)]);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogleCode,
        signInWithAppleCode,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
